import { createPortal } from 'react-dom';

import Button from '@ingka/button';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import { FormBuilder, useForm } from '@sp-saljapro/form-builder';

import type { CustomerInformationDetailsProps, DetailsCustomerData } from '../../types/customer-information.types';
import { buildCustomerInformationDetailsFormConfig } from './customer-information-details.form';

/**
 * Modal form for viewing and editing customer information.
 * @param {DetailsCustomerData} customerData
 * @param {DetailsModalConfig} modalConfig
 * @param {DetailsTranslations} translations
 */
export const CustomerInformationDetails = ({
    customerData,
    modalConfig,
    translations,
}: CustomerInformationDetailsProps) => {
    const { isOpen, readonlyMode = false, onClose, onSave, prefix } = modalConfig;
    const initialCustomerData = customerData ?? {};
    const config = buildCustomerInformationDetailsFormConfig({
        customerData: initialCustomerData,
        readonlyMode,
        translations,
    });
    const resetKey = JSON.stringify({ customerData: initialCustomerData, readonlyMode });
    const { form, status } = useForm({ config, resetKey });

    const handleSave = () => {
        void form.submit({
            onSubmit: (values) => {
                const updatedCustomerData: DetailsCustomerData = {
                    firstName: values.firstName,
                    lastName: values.lastName,
                    businessName: values.businessName,
                    email: values.email,
                    mobile: values.mobile,
                    addressLine1: values.addressLine1,
                    addressLine2: values.addressLine2,
                    zipCode: values.zipCode,
                    city: values.city,
                };
                onSave?.(updatedCustomerData);
                onClose();
            },
        });
    };

    const prefixProp = prefix ? { prefix } : {};

    return createPortal(
        <Modal {...prefixProp} handleCloseBtn={onClose} visible={isOpen}>
            <Sheets
                aria-label={translations?.title ?? 'Customer Information'}
                header={<ModalHeader {...prefixProp} title={translations?.title ?? 'Customer Information Title'} />}
                footer={
                    onSave && !readonlyMode ? (
                        <ModalFooter {...prefixProp}>
                            <Button
                                {...prefixProp}
                                type="primary"
                                text={translations?.saveLabel ?? 'Save'}
                                disabled={!status.isDirty || !status.isValid || status.isValidating}
                                onClick={handleSave}
                            />
                        </ModalFooter>
                    ) : null
                }
            >
                <ModalBody>
                    <FormBuilder form={form} prefix={prefix} />
                </ModalBody>
            </Sheets>
        </Modal>,
        document.body
    );
};
